import { createSlice } from '@reduxjs/toolkit';
import { normalizeLanguageCode, normalizeSettings } from 'utils/language';
import { LOCALS } from 'data';
import { addBalance } from '../balance/balanceOperation';
import {
  googleUser,
  loginWithGoogle,
  registerWithGoogle,
  loginWithApple,
  registerWithApple,
  logIn,
  logOut,
  RefreshUser,
  register,
  resetPassword,
  passwordRecovery,
  deleteUser,
  tokenToState,
  logOutWithError,
  firstEnter,
  changeSettings,
} from './authOperations';

const initialState = {
  user: { email: null },
  settings: { language: LOCALS.EN },
  token: null,
  isLoggedIn: false,
  isRefreshing: false,
  statusEmail: false,
  isLoading: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLanguageLocal(state, action) {
      state.settings = {
        ...state.settings,
        language: normalizeLanguageCode(action.payload),
      };
    },
  },
  extraReducers: builder =>
    builder
      .addCase(register.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.settings = normalizeSettings(action.payload.settings);
        state.token = action.payload.token;
        state.isLoggedIn = true;
      })
      .addCase(logIn.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.settings = normalizeSettings(action.payload.settings);
        state.isLoggedIn = true;
      })
      .addCase(logOut.fulfilled, state => {
        state.user = null;
        state.token = null;
        state.settings = {
          language: normalizeLanguageCode(state.settings.language),
        };
        state.isLoggedIn = false;
      })
      .addCase(deleteUser.fulfilled, state => {
        state.user = null;
        state.token = null;
        state.isLoggedIn = false;
      })
      .addCase(RefreshUser.pending, state => {
        state.isRefreshing = true;
        state.isLoggedIn = false;
      })
      .addCase(RefreshUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.settings = normalizeSettings(action.payload.settings);
        state.token = action.payload.token;
        state.isLoggedIn = true;
        state.isRefreshing = false;
      })
      .addCase(RefreshUser.rejected, state => {
        state.isRefreshing = false;
        state.isLoggedIn = false;
      })
      .addCase(addBalance.pending, state => {
        state.isRefreshing = true;
        state.isLoggedIn = true;
      })
      .addCase(addBalance.fulfilled, (state, action) => {
        state.balance = action.payload.data.balance;
        state.isLoggedIn = true;
        state.isRefreshing = false;
      })
      .addCase(addBalance.rejected, state => {
        state.isRefreshing = false;
        state.isLoggedIn = false;
      })
      .addCase(googleUser.pending, state => {
        state.isRefreshing = false;
        state.isLoggedIn = true;
      })
      .addCase(googleUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.settings = normalizeSettings(action.payload.settings);
        state.isLoggedIn = true;
        state.isRefreshing = false;
      })
      .addCase(googleUser.rejected, state => {
        state.isRefreshing = false;
        state.isLoggedIn = false;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.settings = normalizeSettings(action.payload.settings);
        state.isLoggedIn = true;
        state.isRefreshing = false;
      })
      .addCase(loginWithGoogle.rejected, state => {
        state.isRefreshing = false;
        state.isLoggedIn = false;
      })
      .addCase(registerWithGoogle.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.settings = normalizeSettings(action.payload.settings);
        state.isLoggedIn = true;
        state.isRefreshing = false;
      })
      .addCase(registerWithGoogle.rejected, state => {
        state.isRefreshing = false;
        state.isLoggedIn = false;
      })
      .addCase(loginWithApple.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.settings = normalizeSettings(action.payload.settings);
        state.isLoggedIn = true;
        state.isRefreshing = false;
      })
      .addCase(loginWithApple.rejected, state => {
        state.isRefreshing = false;
        state.isLoggedIn = false;
      })
      .addCase(registerWithApple.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.settings = normalizeSettings(action.payload.settings);
        state.isLoggedIn = true;
        state.isRefreshing = false;
      })
      .addCase(registerWithApple.rejected, state => {
        state.isRefreshing = false;
        state.isLoggedIn = false;
      })
      .addCase(resetPassword.pending, state => {
        state.statusEmail = false;
        state.isLoading = true;
      })
      .addCase(resetPassword.fulfilled, state => {
        state.statusEmail = true;
        state.isLoading = false;
      })
      .addCase(resetPassword.rejected, state => {
        state.statusEmail = false;
        state.isLoading = false;
      })
      .addCase(passwordRecovery.pending, state => {
        state.statusEmail = false;
        state.isLoading = true;
      })
      .addCase(passwordRecovery.fulfilled, state => {
        state.statusEmail = true;
        state.isLoading = false;
      })
      .addCase(passwordRecovery.rejected, state => {
        state.statusEmail = false;
        state.isLoading = false;
      })
      .addCase(tokenToState.fulfilled, (state, action) => {
        state.token = action.payload;
        state.isLoggedIn = true;
        state.isRefreshing = false;
      })
      .addCase(logOutWithError.fulfilled, (state, action) => {
        state.user = null;
        state.token = null;
        state.isLoggedIn = false;
        state.isRefreshing = false;
      })
      .addCase(firstEnter.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.settings = normalizeSettings(action.payload.settings);
      })
      .addCase(changeSettings.fulfilled, (state, action) => {
        state.settings = normalizeSettings(action.payload);
      }),
});

export const { setLanguageLocal } = authSlice.actions;
export const authReducer = authSlice.reducer;
